/** @format */
import React, {useState, useEffect, useCallback} from 'react';
import update from 'immutability-helper';
import SuperAgent from 'superagent';

/**
 * Custom hook for managing design uploader state and actions.
 */
export function useDesignUploaderHandler(props = {}) {
    const {orderId, totalQty} = props;
    const [state, setState] = useState({
        designs_orders: [],
        sort: 'id',
        direction: 'asc',
        saving: false,
        modified: true,
        error: false
    });
    ``;

    // Fetch design orders when component mounts or orderId changes.
    useEffect(() => {
        SuperAgent.get('/account/designs/order-index/' + orderId)
            .set('accept', 'application/json')
            .end((err, response) => {
                if (!err && response && response.body) {
                    setState(prevState =>
                        update(prevState, {designs_orders: {$set: response.body}})
                    );
                }
            });
    }, [orderId]);

    const handleToggleSort = useCallback(
        (sort, directionDefault) => {
            let direction = directionDefault;
            if (state.sort === sort) {
                direction = state.direction === 'asc' ? 'desc' : 'asc';
            }
            setState(prevState =>
                update(prevState, {
                    sort: {$set: sort},
                    direction: {$set: direction}
                })
            );
        },
        [state.sort, state.direction]
    );

    const handleRemoveDesign = useCallback(
        designId => {
            const designIndex = state.designs_orders.findIndex(design => design.id === designId);
            if (designIndex === -1) return;
            setState(prevState =>
                update(prevState, {
                    designs_orders: {$splice: [[designIndex, 1]]},
                    modified: {$set: true}
                })
            );
        },
        [state.designs_orders]
    );

    const handleAddDesign = useCallback(design => {
        setState(prevState =>
            update(prevState, {
                designs_orders: {$push: [design]},
                modified: {$set: true}
            })
        );
    }, []);

    const handleQtyChange = useCallback(
        (designId, e) => {
            const designIndex = state.designs_orders.findIndex(design => design.id === designId);
            if (designIndex === -1) return;
            const design = {...state.designs_orders[designIndex]};
            design.qty = e.target.value.replace(/[^0-9.]/g, '');
            setState(prevState =>
                update(prevState, {
                    modified: {$set: true},
                    designs_orders: {$splice: [[designIndex, 1, design]]}
                })
            );
        },
        [state.designs_orders]
    );

    const handleSaveChanges = useCallback(async () => {
        setState(prevState => update(prevState, {saving: {$set: true}}));
        try {
            await SuperAgent.post('/account/designs/update-order-design-qty/' + orderId)
                .set('accept', 'application/json')
                .send({
                    designs_orders: state.designs_orders.map(design => ({
                        id: design.id,
                        qty: design.qty
                    }))
                });

            setState(prevState =>
                update(prevState, {
                    modified: {$set: false},
                    saving: {$set: false},
                    error: {$set: false}
                })
            );
        } catch (e) {
            setState(prevState =>
                update(prevState, {
                    error: {$set: e?.response?.body?.message || 'An error occurred'},
                    saving: {$set: false}
                })
            );
        }
    }, [orderId, state.designs_orders]);

    const handleAutofill = useCallback(() => {
        const designCount = state.designs_orders.length;
        const perDesignQty = Math.floor(parseInt(totalQty, 10) / parseInt(designCount, 10));
        const updatedDesigns = state.designs_orders.map(design => ({
            ...design,
            qty: perDesignQty
        }));
        setState(prevState =>
            update(prevState, {
                designs_orders: {$set: updatedDesigns},
                modified: {$set: true}
            })
        );
    }, [state.designs_orders, totalQty]);

    return {
        ...state,
        onToggleSort: handleToggleSort,
        onRemoveDesign: handleRemoveDesign,
        onUploadComplete: handleAddDesign,
        onQtyChange: handleQtyChange,
        onSaveChanges: handleSaveChanges,
        onAutofill: handleAutofill
    };
}

/**
 * Higher-Order Component (HOC) that wraps a component with design uploader functionality.
 */
const withDesignUploaderHandler = ComponentContainer => {
    const WithDesignUploaderHandler = props => {
        const uploader = useDesignUploaderHandler(props);

        // Pass both the original props and the uploader state/handlers.
        return <ComponentContainer {...props} {...uploader} />;
    };

    return WithDesignUploaderHandler;
};

export default withDesignUploaderHandler;
